import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image } from "react-bootstrap";
import { useAtom, useAtomValue } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { RESET } from "jotai/utils";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./StartGamePage.scss";
import ImageVersus from "../../images/versus.svg";
import LockIconFilled from "../../components/icons/LockIconFilled";
import GhostButton from "../../components/dashboard-page/GhostButton/GhostButton";
import PlayIconFlilled from "../../components/icons/PlayIconFlilled";
import FixedFooter from "../../components/FixedFooter/FixedFooter";
import CloseIcon from "../../components/icons/CloseIcon";
import Tip from "../../components/game-play/Tip/Tip";
import CancelGameModal from "../../components/game-play/CancelGameModal/CancelGameModal";
import GameModal from "../../components/game-play/GameModal/GameModal";
import LightBulbIcon from "../../components/icons/LightBulbIcon";
import WifiIcon from "../../components/icons/WifiIcon";
import {
  currentPendingTournamentEntryAtom,
  duelEntryAtomWithStorage,
  duelOpponentAtomWithStorage,
  practiceGameEntryAtom,
} from "../../jotai/atoms/tournament";
import { GameType, ROUTES } from "../../utils/constants";
import useTournamentParticipantsQuery from "../../react-query/queries/use-tournament-participants";
import useDuelOpponentQuery from "../../react-query/queries/use-duel-opponent";
import PlayerMatchingCard from "../../components/start-game-page/PlayerMatchingCard/PlayerMatchingCard";
import CountdownIndicator from "../../components/start-game-page/CountdownIndicator/CountdownIndicator";
import { getRandomNumberWithinRange } from "../../utils/helpers";
import { gameTypeAtom } from "../../jotai/atoms/games";
import { queryClient } from "../../react-query/query-client";
import { QUERY_KEYS } from "../../react-query/constants";
import {
  fromBottomAnimation,
  fromLeftAnimation,
  fromRightAnimation,
  fromTopAnimation,
} from "../../utils/animations";
import useEffectOnMountOnly from "../../hooks/useMountEffect";
import { PENDING_GAME_MARKER } from "../../components/dashboard-page/PendingGameCard/PendingGameCard";
import {
  Tournament,
  TournamentType,
} from "@nestcoinco/hyper-api-gateway-api-client";
import useAuth from "../../hooks/useAuth";

interface StartGamePageProps {
  tournamentDetails?: Tournament;
  gameName?: string;
}

enum GameState {
  FINDING_PLAYERS,
  MATCHED_PLAYERS,
  MATCHING_TIME_LIMIT_REACHED,
  STARTING_GAME,
  START_GAME_INSTANTLY,
}

const FIND_REAL_MATCH_UPPER_THRESHOLD = 6;
const FIND_REAL_MATCH_LOWER_THRESHOLD = 4;
const FIND_FAST_MATCH_UPPER_THRESHOLD = 3;
const FIND_FAST_MATCH_LOWER_THRESHOLD = 1;
const MAX_GAMERS = 2;

const timeToMatchForRealPlayer = getRandomNumberWithinRange(
  FIND_REAL_MATCH_UPPER_THRESHOLD,
  FIND_REAL_MATCH_LOWER_THRESHOLD
);
const timeToMatchForFastMatch = getRandomNumberWithinRange(
  FIND_FAST_MATCH_UPPER_THRESHOLD,
  FIND_FAST_MATCH_LOWER_THRESHOLD
);
const COUNTDOWN_INTERVAL = 1500;
const DEFAULT_ANIMATION_DURATION = 0.5;
const DEFAULT_ANIMATION_DELAY = 0.1;

const MotionPlayerMatchingCard = motion(PlayerMatchingCard);
const MotionImage = motion(Image);
const MotionFixedFooter = motion(FixedFooter);

function StartGamePage({ tournamentDetails, gameName }: StartGamePageProps) {
  const { t } = useTranslation("games");
  const navigate = useNavigate();
  const { tournamentId } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const [matchingCountdown, setMatchingCountdown] = useState(7);
  const [gameStartingCountdown, setGameStartingCountdown] = useState(5);
  const [gameState, setGameState] = useState(GameState.FINDING_PLAYERS);
  const [showModal, setShowModal] = useState(false);
  const [hasAttemptedMatching, setHasAttemptedMatching] = useState(false);
  const matchingTimeoutRef = useRef<NodeJS.Timeout>();
  const gameStartingTimeoutRef = useRef<NodeJS.Timeout>();
  const [isGameModalVisible, setIsGameModalVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [practiceGameEntry] = useAtom(practiceGameEntryAtom);
  const gameType = useAtomValue(gameTypeAtom);
  const [duelOpponent, setDuelOpponent] = useAtom(duelOpponentAtomWithStorage);
  const duelEntry = useAtomValue(duelEntryAtomWithStorage);
  const currentPendingTournamentEntry = useAtomValue(
    currentPendingTournamentEntryAtom
  );
  const { userProfile } = useAuth();
  const loggedInUsername = userProfile?.username;
  const loggedInUserAvatar = userProfile?.avatar?.url;

  const isPracticeGame =
    gameType === GameType.PRACTICE &&
    !!tournamentId?.includes(GameType.PRACTICE);
  const isMultiplayerTournament =
    tournamentDetails?.type === TournamentType.MULTI;
  const isReplayingPendingGame = type === PENDING_GAME_MARKER;

  const {
    name: tournamentGameName,
    slug: tournamentGameSlug,
    orientation: tournamentGameOrientation,
  } = tournamentDetails?.game ?? {};

  const gameOrientation =
    tournamentGameOrientation ?? practiceGameEntry?.game?.orientation;

  const canTryForRealUser =
    matchingCountdown >= FIND_REAL_MATCH_LOWER_THRESHOLD &&
    !isPracticeGame &&
    !isMultiplayerTournament;

  const { data: realTournamentParticipants } = useTournamentParticipantsQuery({
    tournamentId: tournamentId || "",
    enabled: canTryForRealUser,
  });

  const canTryForFastMatch =
    matchingCountdown <= FIND_FAST_MATCH_UPPER_THRESHOLD &&
    matchingCountdown >= FIND_FAST_MATCH_LOWER_THRESHOLD - 1 &&
    (!realTournamentParticipants ||
      realTournamentParticipants?.records.length !== MAX_GAMERS) &&
    !isPracticeGame &&
    !isMultiplayerTournament;

  const redirectUrl = tournamentGameSlug
    ? `${ROUTES.GAMES.ROOT}/${tournamentGameSlug}${ROUTES.GAMES.PLAY_GAME}`
    : ROUTES.GAMES.ROOT;

  const entryId = isReplayingPendingGame
    ? currentPendingTournamentEntry?.id || ""
    : duelEntry?.id || "";

  let gameUrl;
  if (isReplayingPendingGame)
    gameUrl = currentPendingTournamentEntry?.gamePlayUrl;
  else if (gameType === GameType.PRACTICE)
    gameUrl = practiceGameEntry?.gamePlayUrl;
  else gameUrl = duelEntry?.gamePlayUrl;

  const { data: fastMatchOpponent } = useDuelOpponentQuery({
    tournamentId: tournamentId || "",
    enabled: canTryForFastMatch,
  });

  const isRealPlayer =
    realTournamentParticipants &&
    realTournamentParticipants.records.length === MAX_GAMERS;
  const timeToMatch = isRealPlayer
    ? timeToMatchForRealPlayer
    : timeToMatchForFastMatch;

  let tipText;
  let tipIcon;
  if (gameState === GameState.FINDING_PLAYERS) {
    if (matchingCountdown <= FIND_FAST_MATCH_UPPER_THRESHOLD) {
      tipText = t("make_sure_you_have_good_internet");
      tipIcon = <WifiIcon fill="white" />;
    } else if (matchingCountdown <= FIND_REAL_MATCH_UPPER_THRESHOLD - 1) {
      tipText = t("tap_start_game_to_skip_the_countdown");
      tipIcon = <LightBulbIcon fill="white" />;
    } else {
      tipText = t("you_will_get_the_same_gameplay_as_your_opponent");
      tipIcon = <LockIconFilled fill="white" />;
    }
  }

  if (
    gameState === GameState.MATCHED_PLAYERS ||
    gameState === GameState.MATCHING_TIME_LIMIT_REACHED ||
    gameState === GameState.STARTING_GAME
  ) {
    tipIcon = <LightBulbIcon fill="white" />;
    if (gameStartingCountdown <= FIND_FAST_MATCH_UPPER_THRESHOLD) {
      tipText = t("score_higher_to_win_the_game");
    } else {
      tipText = t("do_not_get_disconnected_during_the_game");
    }
  }

  const openCancelGameModal = () => {
    setIsPaused(true);
    setShowModal(true);
  };

  const closeCancelGameModal = () => {
    setIsPaused(false);
    setShowModal(false);
  };

  const startGame = () => {
    setGameState(GameState.START_GAME_INSTANTLY);
  };

  const myOpponent = useMemo(
    () =>
      realTournamentParticipants?.records.find(
        (participant) => participant.username !== loggedInUsername
      ),
    [loggedInUsername, realTournamentParticipants?.records]
  );

  const handleOpponentMatching = useCallback(() => {
    if (
      realTournamentParticipants &&
      realTournamentParticipants.records.length === MAX_GAMERS
    ) {
      myOpponent &&
        setDuelOpponent({
          username: myOpponent.username,
          avatar: myOpponent.avatarUrl,
        });
    } else if (fastMatchOpponent) {
      if (fastMatchOpponent?.success) {
        setDuelOpponent({
          username: fastMatchOpponent.data?.username,
          avatar: fastMatchOpponent.data?.avatarUrl,
        });
      } else {
        setDuelOpponent(RESET);
      }
    }
  }, [
    fastMatchOpponent,
    myOpponent,
    realTournamentParticipants,
    setDuelOpponent,
  ]);

  useEffect(() => {
    clearTimeout(matchingTimeoutRef.current);
    if (isPaused || gameState > GameState.FINDING_PLAYERS) return;

    const timeoutId = setTimeout(() => {
      setMatchingCountdown((prev) => (prev < 1 ? prev : prev - 1));
    }, COUNTDOWN_INTERVAL);

    matchingTimeoutRef.current = timeoutId;

    if (matchingCountdown <= timeToMatch) {
      handleOpponentMatching();
      setMatchingCountdown(0);
      setHasAttemptedMatching(true);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    gameState,
    handleOpponentMatching,
    isPaused,
    matchingCountdown,
    timeToMatch,
  ]);

  useEffect(() => {
    if (!hasAttemptedMatching) return;
    const timeoutId = setTimeout(() => {
      if (duelOpponent?.username) {
        setGameState(GameState.MATCHED_PLAYERS);
      } else {
        setGameState(GameState.MATCHING_TIME_LIMIT_REACHED);
      }
    }, 250);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [duelOpponent?.username, hasAttemptedMatching]);

  useEffect(() => {
    if (matchingCountdown > 0) return;

    clearTimeout(gameStartingTimeoutRef.current);
    if (isPaused) return;

    const timeoutId = setTimeout(() => {
      setGameStartingCountdown((prev) => (prev < 1 ? prev : prev - 1));
    }, 1000);
    gameStartingTimeoutRef.current = timeoutId;

    if (
      (gameState === GameState.MATCHED_PLAYERS ||
        gameState === GameState.MATCHING_TIME_LIMIT_REACHED) &&
      gameStartingCountdown <= 2
    ) {
      setGameState(GameState.STARTING_GAME);
    }

    if (gameStartingCountdown <= 0) {
      setIsGameModalVisible(true);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [gameStartingCountdown, gameState, isPaused, matchingCountdown]);

  useEffect(() => {
    if (
      (!isReplayingPendingGame && duelEntry) ||
      gameType === GameType.PRACTICE ||
      (isReplayingPendingGame && currentPendingTournamentEntry)
    ) {
      return;
    }
    navigate(redirectUrl);
  }, [
    duelEntry,
    navigate,
    redirectUrl,
    gameType,
    isReplayingPendingGame,
    currentPendingTournamentEntry,
  ]);

  useEffectOnMountOnly(() => {
    queryClient.resetQueries({
      queryKey: [QUERY_KEYS.TOURNAMENT.TOURNAMENT_PARTICIPANTS, tournamentId],
    });
    setDuelOpponent(RESET);
  });

  if (
    (isPracticeGame ||
      isGameModalVisible ||
      gameState === GameState.START_GAME_INSTANTLY) &&
    gameUrl &&
    !isPaused
  ) {
    return (
      <GameModal
        gameLink={gameUrl}
        gameName={gameName}
        orientation={gameOrientation}
      />
    );
  }

  return (
    <div className="start-game-page MobileOnly position-relative">
      <motion.div
        className="position-relative"
        variants={fromTopAnimation}
        initial="initial"
        animate="final"
        transition={{
          ease: "easeIn",
          duration: DEFAULT_ANIMATION_DURATION,
          delay: DEFAULT_ANIMATION_DELAY,
        }}
      >
        {gameState === GameState.FINDING_PLAYERS && (
          <GhostButton
            className="close-btn text-white"
            onClick={openCancelGameModal}
          >
            <CloseIcon fill="#FFFFFF17" />
          </GhostButton>
        )}

        <Image
          src={tournamentDetails?.game.thumbnail}
          alt="game banner"
          width={34}
          className="mx-auto mb-2"
        />
        <p className="game-title slashed-zero">{tournamentGameName}</p>
        <div className="heading-box" style={{ minHeight: 150 }}>
          <h1 className="main-heading slashed-zero">
            {(gameState === GameState.FINDING_PLAYERS ||
              gameState === GameState.MATCHING_TIME_LIMIT_REACHED) && (
              <FindingPlayersHeading />
            )}
            {gameState === GameState.MATCHED_PLAYERS && (
              <PlayerMatchedHeading />
            )}
            {gameState === GameState.STARTING_GAME && <GameStartingHeading />}
          </h1>
          {matchingCountdown > 0 && (
            <p className="countdown slashed-zero">
              <span>{matchingCountdown}s</span>
            </p>
          )}
        </div>
      </motion.div>
      <div className="d-flex  gap-4 position-relative px-3">
        <MotionPlayerMatchingCard
          avatar={loggedInUserAvatar}
          username={loggedInUsername}
          variants={fromLeftAnimation}
          initial="initial"
          animate="final"
          transition={{
            ease: "easeIn",
            duration: DEFAULT_ANIMATION_DURATION,
            delay: DEFAULT_ANIMATION_DELAY,
          }}
        />
        <MotionPlayerMatchingCard
          isOpponent
          isSearching={!duelOpponent}
          avatar={duelOpponent?.avatar}
          username={duelOpponent?.username}
          variants={fromRightAnimation}
          initial="initial"
          animate="final"
          transition={{
            ease: "easeIn",
            duration: DEFAULT_ANIMATION_DURATION,
            delay: DEFAULT_ANIMATION_DELAY,
          }}
        />
        <div className="versus-box">
          <MotionImage
            src={ImageVersus}
            alt=""
            aria-label="versus"
            initial={{
              height: 10,
              width: 10,
            }}
            animate={{ height: 70, width: 70 }}
            transition={{
              duration: DEFAULT_ANIMATION_DURATION,
              delay: DEFAULT_ANIMATION_DELAY + 0.2,
            }}
          />
        </div>
      </div>
      <MotionFixedFooter
        className="px-4"
        variants={fromBottomAnimation}
        initial="initial"
        animate="final"
        transition={{
          ease: "easeIn",
          duration: DEFAULT_ANIMATION_DURATION,
          delay: DEFAULT_ANIMATION_DELAY,
        }}
      >
        <Tip icon={tipIcon} className="mb-5">
          {t("tip")}: {tipText}
        </Tip>
        <div className="indicator-container">
          {gameState === GameState.FINDING_PLAYERS ? (
            <GhostButton className="start-game-btn" onClick={startGame}>
              <PlayIconFlilled fill="white" /> <span>{t("start_game")}</span>
            </GhostButton>
          ) : (
            <CountdownIndicator
              style={{
                position: "fixed",
                zIndex: 20,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              className="MobileOnly"
            />
          )}
        </div>
      </MotionFixedFooter>
      <CancelGameModal
        show={showModal}
        onHide={closeCancelGameModal}
        redirectUrl={redirectUrl}
        entryId={entryId}
        tournamentDetails={tournamentDetails}
      />
    </div>
  );
}

export default StartGamePage;

function FindingPlayersHeading() {
  const { t } = useTranslation("games");

  return (
    <>
      {t("finding_players_of")} <br />
      <span className="darker">{t("your_skill_level")}</span>
    </>
  );
}

function PlayerMatchedHeading() {
  const { t } = useTranslation("games");

  return (
    <>
      {t("you_have_been")} <br />
      <span className="darker">{t("matched")}! </span>
    </>
  );
}

function GameStartingHeading() {
  const { t } = useTranslation("games");

  return <>{t("game_is_starting")}</>;
}
